import {type PropsWithChildren, forwardRef} from 'react';
import {cn} from '../lib/cn';
import {HALO_ICON_STATUS} from './constants';

const statusStyles = {
  [HALO_ICON_STATUS.inactive]: {
    background: 'bg-[var(--chekin-color-surface-input-empty)]',
    color: 'text-[var(--chekin-color-gray-2)]',
  },
  [HALO_ICON_STATUS.active]: {
    background: 'bg-[var(--chekin-color-surface-autocomplete)]',
    color: 'text-[var(--chekin-color-brand-blue)]',
  },
  [HALO_ICON_STATUS.success]: {
    background: 'bg-[var(--chekin-green-50)]',
    color: 'text-[var(--chekin-green-600)]',
  },
  [HALO_ICON_STATUS.danger]: {
    background: 'bg-[var(--chekin-red-100)]',
    color: 'text-[var(--error-message-color)]',
  },
} as const;

export interface HaloIconProps extends PropsWithChildren {
  status?: keyof typeof statusStyles;
  size?: 'M' | 'L' | 'XL';
  className?: string;
  variant?: 'default' | 'secondary';
}

export const HaloIcon = forwardRef<HTMLDivElement, HaloIconProps>(
  (
    {
      children,
      variant = 'default',
      status = HALO_ICON_STATUS.inactive,
      size = 'M',
      className,
    },
    ref,
  ) => {
    const styles = statusStyles[status];

    return (
      <div
        ref={ref}
        className={cn(
          'halo-icon flex items-center justify-center rounded-[50%]',
          styles.color,
          {
            'h-6 w-6 [&>svg]:h-4 [&>svg]:w-4': size === 'M',
            'h-8 w-8 [&>svg]:h-5 [&>svg]:w-5': size === 'L',
            'h-11 w-11 [&>svg]:h-5 [&>svg]:w-5': size === 'XL',
            [styles.background]: variant === 'default',
          },
          className,
        )}
      >
        {children}
      </div>
    );
  },
);

HaloIcon.displayName = 'HaloIcon';
